"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.propTypes = exports.defaultProps = exports.default = exports.BottomSheet = void 0;
require("./BottomSheet.css");
var _classnames = _interopRequireDefault(require("classnames"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _OverlayDefaultContent = _interopRequireDefault(require("../OverlayDefaultContent/OverlayDefaultContent"));
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * BottomSheet component
 *
 * @method
 * @param { BottomSheetProps } props
 * @returns BottomSheet
 */

const BottomSheet = props => {
  const {
    ariaLabel,
    ariaLabelledby,
    isOpen = defaultProps.isOpen,
    title,
    titleTag,
    description,
    externalFooterChildrenElements,
    externalCloseActionElement,
    children,
    className,
    panelRef,
    heightPercentageOverride,
    // number 0–100 (vh)
    variant = defaultProps.variant,
    supportText
  } = props;
  const internalSheetRef = (0, _react.useRef)(null);
  const bottomSheetRef = panelRef ?? internalSheetRef;
  const componentClassName = (0, _classnames.default)('pal-c-BottomSheet', {
    'pal-c-BottomSheet__open': isOpen,
    'pal-c-BottomSheet__isClosing': !isOpen
  }, className);
  (0, _react.useEffect)(() => {
    if (!bottomSheetRef.current) {
      return;
    }
    const resolvedHeight = heightPercentageOverride ?? 50;
    bottomSheetRef.current.style.maxHeight = `${resolvedHeight}vh`;
  }, [heightPercentageOverride]);
  const defaultContent = /*#__PURE__*/_react.default.createElement(_OverlayDefaultContent.default, {
    title: title,
    titleTag: titleTag,
    description: description,
    showBackButton: false,
    showCloseButton: true,
    externalFooterChildrenElements: externalFooterChildrenElements,
    externalCloseActionElement: externalCloseActionElement,
    supportText: supportText
  }, children);
  return /*#__PURE__*/_react.default.createElement("div", {
    className: componentClassName,
    ref: bottomSheetRef,
    role: "dialog",
    "aria-modal": "true",
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabel ? undefined : ariaLabelledby
  }, variant === 'default' && defaultContent, variant === 'custom' && /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-BottomSheet__customContent"
  }, children));
};

/**
 * Property type definitions
 * @typedef BottomSheetProps
 * @type {object}
 * @property {string} ariaLabel - value for aria label
 * @property {string} ariaLabelledby -  HTML id for aria-labelledby
 * @property {boolean} isOpen - Sets the open state for the BottomSheet.
 * @property {string} title - Title text of the BottomSheet.
 * @property {string} description - Description text of the BottomSheet.
 * @property {object} externalCloseActionElement - Close action element with business and DC logic from web-site.
 * @property {string} className - Sets the additional className to the BottomSheet container div.
 * @property {number} heightPercentageOverride - overrides default 50vh max-height
 * @property {object} externalFooterChildrenElements - Footer content for the BottomSheet.
 * @property {object} children - Children content for the BottomSheet.
 * @property {any} panelRef - Sets the ref of the BottomSheet container.
 * @property {string} variant - Sets the variant of the BottomSheet content (default, custom)
 * @property {string} supportText - Displays support text below the footer actions for default variant.
 * @property {string} titleTag - HTML tag of the title - default set in TitlesDefault
 *
 */
exports.BottomSheet = BottomSheet;
const propTypes = exports.propTypes = {
  /**
   * value for aria label
   */
  ariaLabel: _propTypes.default.string,
  /**
   * HTML id for aria-labelledby
   */
  ariaLabelledby: _propTypes.default.string,
  /**
   * Sets the open state for the BottomSheet.
   */
  isOpen: _propTypes.default.bool,
  /**
   * Title text of the BottomSheet.
  */
  title: _propTypes.default.string,
  /**
   * HTML tag of the title - default set in TitlesDefault
   */
  titleTag: _propTypes.default.string,
  /**
   * Description text of the BottomSheet.
  */
  description: _propTypes.default.string,
  /**
   * Footer content for the BottomSheet.
   */
  externalFooterChildrenElements: _propTypes.default.object,
  /**
   * Children content for the BottomSheet
   */
  children: _propTypes.default.object,
  /**
   * Close action element with business and DC logic from web-site.
  */
  externalCloseActionElement: _propTypes.default.object,
  /**
   * Sets the additional className to the BottomSheet container div.
  */
  className: _propTypes.default.string,
  /**
   * Overrides default 50vh max-height (pass a number 0–100 for vh).
   */
  heightPercentageOverride: _propTypes.default.number,
  /**
   * Sets the ref of the BottomSheet container.
   */
  panelRef: _propTypes.default.any,
  /**
   * Sets the variant of content rendered for the BottomSheet (default, custom)
   * - default:  uses OverlayDefaultContent component to render default BottomSheet with sticky header and footer
   * - custom: can be used to configured the BottomSheet with custom content for header, body and footer
   */
  variant: _propTypes.default.oneOf(['default', 'custom']),
  /**
   * Displays support text below the footer actions for default variant.
   */
  supportText: _propTypes.default.string
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {boolean} isOpen - isOpen: false - sets the default open state of the BottomSheet to be false.
 * @property {string} variant - variant: 'default' - sets the default variant to render OverlayDefaultContent component.
 */
const defaultProps = exports.defaultProps = {
  isOpen: false,
  variant: 'default'
};
BottomSheet.propTypes = propTypes;
BottomSheet.defaultProps = defaultProps;
var _default = exports.default = BottomSheet;